/**
 * Shared scroll-reveal engine.
 *
 * Content that starts at `opacity: 0` must be guaranteed to become visible.
 * An IntersectionObserver on its own does not guarantee that: on a fast scroll
 * or an anchor jump the browser can coalesce updates and never report an
 * element as intersecting, which would leave it invisible forever.
 *
 * So reveal is driven by a monotonic test — "has the top of this element ever
 * been above the bottom of the viewport?" — evaluated from three independent
 * triggers: registration, the observer, and the shared scroll driver's
 * rAF-throttled pass. Any one of them is enough, and once an element is
 * revealed it is dropped from the registry.
 */
import { onScroll } from './scrollDriver';

// Reveal slightly before the element's top edge reaches the bottom of the
// viewport, so the motion reads as anticipation rather than a late pop.
const TRIGGER_RATIO = 0.94;

/** @type {Map<Element, () => void>} */
const registry = new Map();

let observer = null;
let unsubscribe = null;

const viewportHeight = () =>
  window.innerHeight || document.documentElement.clientHeight || 0;

/** Monotonic: true once the element has been scrolled to, and never false after. */
function hasBeenReached(el) {
  const rect = el.getBoundingClientRect();
  return rect.top <= viewportHeight() * TRIGGER_RATIO;
}

function release(el) {
  const callback = registry.get(el);
  if (!callback) return;
  registry.delete(el);
  observer?.unobserve(el);
  callback();
}

function sweep() {
  registry.forEach((_, el) => {
    if (hasBeenReached(el)) release(el);
  });
}

function setup() {
  if (!unsubscribe) unsubscribe = onScroll(sweep);

  if (!observer && typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Trust the geometry rather than `isIntersecting`: an element the
          // viewport flew past reports false, but has still been reached.
          if (entry.isIntersecting || hasBeenReached(entry.target)) {
            release(entry.target);
          }
        });
      },
      { threshold: 0 }
    );
  }
}

/**
 * Watch an element and invoke `onReveal` once it has been scrolled to.
 * @returns {() => void} unregister
 */
export function observeReveal(el, onReveal) {
  // No DOM APIs (SSR, ancient browser) — show the content immediately.
  if (!el || typeof window === 'undefined') {
    onReveal();
    return () => {};
  }

  setup();
  registry.set(el, onReveal);

  if (hasBeenReached(el)) {
    release(el);
    return () => {};
  }

  observer?.observe(el);

  return () => {
    registry.delete(el);
    observer?.unobserve(el);
  };
}

export default observeReveal;
